import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {tagsAPI} from 'entities/monitor';
import {domainMonitorAPI} from 'features/domain/api/domainMonitorAPI';
import {buildDomainMonitorSettings, getDomainMonitorFormDefaults} from 'features/domain/lib/domainMonitorFormHelper';
import {useIsFreeTariff, userSelector} from 'entities/user';
import {isTranslate} from 'shared/lib/lang';

import CreateDomainMonitorType from './DomainMonitor/CreateDomainMonitorType';
import IDomainMonitorOutput from './DomainMonitor/IDomainMonitor';
import useCanCreateDomainMonitor from './useCanCreateDomainMonitor';

interface IUseDomainMonitorFormParams {
    item?: IDomainMonitorOutput,
    onSuccess: () => void,
    onLimitExceeded?: () => void,
}

export interface IUseDomainMonitorForm {
    form: UseFormReturn<CreateDomainMonitorType>,
    isNew: boolean,
    isSubmitting: boolean,
    freeTariff: boolean,
    submit: ReturnType<UseFormReturn<CreateDomainMonitorType>['handleSubmit']>,
}

export const useDomainMonitorForm = ({
    item,
    onSuccess,
    onLimitExceeded,
}: IUseDomainMonitorFormParams): IUseDomainMonitorForm => {
    const user = useSelector(userSelector);
    const canCreate = useCanCreateDomainMonitor();
    const freeTariff = useIsFreeTariff();
    const [addDomainMonitor, {isLoading: isLoadingCreate}] = domainMonitorAPI.useAddDomainMonitorMutation();
    const [updateDomainMonitor, {isLoading: isLoadingUpdate}] = domainMonitorAPI.useUpdateDomainMonitorMutation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();

    const isNew = !item;

    const form = useForm<CreateDomainMonitorType>({
        mode: 'onChange',
        defaultValues: getDomainMonitorFormDefaults(item, user),
    });

    const onSubmit: SubmitHandler<CreateDomainMonitorType> = async({domain, ...rest}) => {
        const data = buildDomainMonitorSettings(rest, isTranslate());

        try {
            if (item) {
                await updateDomainMonitor({uuid: item.uuid, ...data}).unwrap();
                await refetchTags();
                toast(t`Монитор домена изменён`);
            } else {
                await addDomainMonitor({...data, domain}).unwrap();
                await refetchTags();
                toast(t`Монитор домена добавлен`);
            }
            onSuccess();
        } catch (error) {
            if (data.active && !canCreate) {
                onLimitExceeded?.();
            }
        }
    };

    return {
        form,
        isNew,
        isSubmitting: isLoadingCreate || isLoadingUpdate,
        freeTariff,
        submit: form.handleSubmit(onSubmit),
    };
};

export default useDomainMonitorForm;
